import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { moneyInputValue, parseMoney } from "@/components/MoneyInput";
import { api, formatApiError } from "@/lib/api";
import { isDebtName, periodOf, typeFor, type BudgetItemRow, type Frequency, type Section } from "@/lib/budget";
import type { Category, CategoryGroup } from "@/lib/finance";

export interface Draft { key: string; id?: string; name: string; kind: Section; category_id: string; group: CategoryGroup; amountText: string; frequency: Frequency; payMonth: string; loanId?: string | null }
type Patch = Partial<Pick<Draft, "name" | "category_id" | "group" | "amountText" | "frequency" | "payMonth">>;

export const draftAmount = (d: Draft, _month: string) => (parseMoney(d.amountText) || 0) / periodOf(d.frequency);

export interface BudgetCtl {
  month: string;
  dirty: boolean;
  draft: Draft[] | null;
  categories: Category[];
  editing: boolean;
  saving: boolean;
  startEdit: () => void;
  cancel: () => void;
  update: (key: string, patch: Patch) => void;
  remove: (key: string) => void;
  add: (name: string, kind: Section, category_id?: string, group?: CategoryGroup) => void;
  save: (scope: PlanScope) => Promise<boolean>;
  validate: () => boolean;
}

export type PlanScope = "only" | "forward";

export interface PlanTotals { income: number; essentials: number; comfort: number; debts: number; savings: number; housing: number; assigned: number; unassigned: number }

export function planTotals(draft: Draft[], categories: Category[], month: string): PlanTotals {
  const housingIds = new Set(categories.filter((c) => c.name.trim().toLowerCase() === "vivienda").map((c) => c.id));
  const sum = (pred: (d: Draft) => boolean) => draft.filter(pred).reduce((s, d) => s + draftAmount(d, month), 0);
  const income = sum((d) => d.kind === "income");
  const debts = sum((d) => d.kind === "expense" && d.group === "debt");
  const essentials = sum((d) => d.kind === "expense" && d.group === "essentials");
  const comfort = sum((d) => d.kind === "expense" && d.group === "comfort");
  const savings = sum((d) => d.kind === "savings");
  const housing = sum((d) => d.kind === "expense" && housingIds.has(d.category_id));
  const assigned = essentials + comfort + debts + savings;
  return { income, essentials, comfort, debts, savings, housing, assigned, unassigned: income - assigned };
}

const toDraft = (i: BudgetItemRow): Draft => ({ key: i.id, id: i.id, name: i.name, kind: i.kind, category_id: i.category_id, group: i.group, amountText: moneyInputValue(i.amount), frequency: i.frequency ?? "monthly", payMonth: i.pay_month ?? "", loanId: i.loan_account_id ?? null });
const sig = (ds: Draft[]) => JSON.stringify(ds.map((d) => [d.id ?? "", d.name.trim(), d.category_id, d.group, parseMoney(d.amountText) || 0, d.frequency, d.frequency === "monthly" ? "" : d.payMonth]));
let seq = 0;

export function useBudgetDraft(month: string): BudgetCtl {
  const [items, setItems] = useState<BudgetItemRow[] | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [draft, setDraft] = useState<Draft[] | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setDraft(null);
      const [i, c] = await Promise.all([api.get<{ items: BudgetItemRow[] }>("/budget/plan", { params: { month } }), api.get<Category[]>("/categories")]);
      setItems(i.data.items);
      setCategories(c.data);
      setDraft(i.data.items.map(toDraft));
    } catch (e) { toast.error(formatApiError(e)); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const update = (key: string, patch: Patch) => setDraft((ds) => (ds ?? []).map((d) => (d.key === key ? { ...d, ...patch } : d)));
  const remove = (key: string) => setDraft((ds) => (ds ?? []).filter((d) => d.key !== key));

  const defaultCategory = (name: string, kind: Section): Category | undefined => {
    const active = categories.filter((c) => !c.is_archived);
    if (kind === "income") return active.find((c) => c.type === "income" && c.name.toLowerCase() === name.trim().toLowerCase());
    if (kind === "savings") return active.find((c) => c.type === "transfer");
    return active.find((c) => c.type === "expense" && c.name === "Otros e imprevistos") ?? active.find((c) => c.type === "expense");
  };
  const add = (name: string, kind: Section, category_id?: string, group?: CategoryGroup) => {
    const cat = category_id ? categories.find((c) => c.id === category_id) : defaultCategory(name, kind);
    const g: CategoryGroup = kind === "income" ? "income" : kind === "savings" ? "savings_investment"
      : group ?? typeFor(name, cat);
    seq += 1;
    setDraft((ds) => [...(ds ?? []), { key: `new-${seq}`, name: name.trim(), kind, category_id: cat?.id ?? "", group: g, amountText: "", frequency: "monthly", payMonth: month }]);
  };

  const amountOf = (d: Draft) => (d.amountText.trim() === "" ? 0 : parseMoney(d.amountText));
  const validate = () => {
    const rows = draft ?? [];
    if (!rows.length) { toast.error("Agrega al menos una línea"); return false; }
    if (rows.some((d) => !d.name.trim())) { toast.error("Cada línea necesita un nombre"); return false; }
    if (rows.some((d) => d.kind !== "income" && !d.category_id)) { toast.error("Elige la categoría de cada línea"); return false; }
    if (rows.some((d) => { const n = amountOf(d); return Number.isNaN(n) || n < 0; })) { toast.error("Revisa los montos"); return false; }
    return true;
  };

  const save = async (scope: PlanScope) => {
    if (!validate()) return false;
    setSaving(true);
    try {
      const lines = (draft ?? []).map((d) => ({ id: d.id, name: d.name.trim(), kind: d.kind, category_id: d.category_id || undefined,
        group: d.kind === "expense" ? d.group : undefined, amount: amountOf(d), frequency: d.frequency,
        pay_month: d.frequency === "monthly" ? undefined : d.payMonth || month }));
      await api.put("/budget/plan", { month, scope, lines });
      toast.success(scope === "only" ? "Guardado solo para este mes" : "Guardado de este mes en adelante");
      setEditing(false);
      await load();
      return true;
    } catch (e) { toast.error(formatApiError(e)); return false; }
    finally { setSaving(false); }
  };

  return {
    month, dirty: !!draft && !!items && sig(draft) !== sig(items.map(toDraft)),
    draft, categories, editing, saving, update, remove, add, save, validate,
    startEdit: () => setEditing(true),
    cancel: () => { setDraft((items ?? []).map(toDraft)); setEditing(false); },
  };
}
